import React, { useState, useEffect } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { textError, percentageError } from "../../../utils/validationMessage";
import style from "../../../styles/brsr/general.module.css";
import { api } from "../../../utils/api";
import { useParams } from "react-router-dom";
import { brsrReview, reviewBRSR } from "../../../utils/companyId";
import ReviewBox from "../../Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";
import BRSRFile from "../../Common/BRSRFile";

const CustomTableRow = ({
  label,
  fields,
  formData,
  handleInputChange,
  showTotal,
  placeholder,
  errors,
  disabled,
}) => (
  <TableRow sx={{ height: "20px" }}>
    <TableCell>{label}</TableCell>
    {fields.map((field) => (
      <TableCell key={field}>
        <TextField
          name={field}
          value={formData[field]}
          onChange={handleInputChange}
          disabled={disabled}
          size="small"
          placeholder={placeholder}
          InputProps={{ disableUnderline: true }}
          sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}
          error={!!errors[field]} // Show error styling if there's an error for the field
        />
      </TableCell>
    ))}
    {showTotal && (
      <TableCell>
        <TextField
          disabled
          placeholder="Total"
          value={
            fields.some(
              (field) =>
                formData[field] !== "" &&
                formData[field] !== null &&
                formData[field] !== undefined,
            )
              ? fields.reduce(
                  (sum, field) => sum + Number(formData[field] || 0),
                  0,
                )
              : ""
          }
          sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}
          InputProps={{ disableUnderline: true }}
        />
      </TableCell>
    )}
  </TableRow>
);

const GeneralDisclosureForm = ({ onSave, hide, removeUpload = false }) => {
  const { id } = useParams();
  const [modalupload, setModalUpload] = useState({
    numberOfLocationsDoc: false,
    marketsServedDoc: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [progress, setUploadProgress] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    nationalPlants: "",
    nationalOffices: "",
    internationalPlants: "",
    internationalOffices: "",
    nationalStates: "",
    internationalCountries: "",
    exportPercentage: "",
    customerBrief: "",
  });
  const [errors, setErrors] = useState({}); // State to track errors
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [operationsData, setOperationsData] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [formData1, setFormData1] = useState({});

  const type = "SectionA"; // Query parameter for type

  // Fetch existing data if ID is present
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/brsr/get?type=SectionA&id=${id}`);
        // Ensure nested objects are accessed properly

        if (
          response?.data?.data?.year
            ? response?.data?.data?.year < currentYear - 1
            : false || !brsrReview
        )
          setDisabled(true);

        const operations = response?.data.data.BRSRSectionA?.operations || {};
        setFormData1(operations);
        if (response?.data?.data) {
          setFormData({
            nationalPlants:
              operations.numberOfLocations?.national?.plants ?? "",
            nationalOffices:
              operations.numberOfLocations?.national?.offices ?? "",
            internationalPlants:
              operations.numberOfLocations?.international?.plants ?? "",
            internationalOffices:
              operations.numberOfLocations?.international?.offices ?? "",
            nationalStates: operations.marketsServed?.nationalStates ?? "",
            internationalCountries:
              operations.marketsServed?.internationalCountries ?? "",
            exportPercentage:
              operations.marketsServed?.exportsContribution ?? "",
            customerBrief: operations.marketsServed?.customerTypes ?? "",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, type]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "exportPercentage") {
      // Allow only numbers and one decimal point
      if (!/^\d*\.?\d*$/.test(value)) return;

      // Allow empty value while typing
      if (value !== "" && Number(value) > 100) return;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));

      return;
    }

    const numericFields = [
      "nationalPlants",
      "nationalOffices",
      "internationalPlants",
      "internationalOffices",
      "nationalStates",
      "internationalCountries",
    ];

    if (numericFields.includes(name)) {
      if (value !== "" && !/^\d+$/.test(value)) {
        return;
      }
    }

    const formattedValue = numericFields.includes(name)
      ? value === ""
        ? ""
        : Number(value)
      : value;

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Number fields validation
    const numberFields = [
      { field: "nationalPlants", label: "National - Number of Plants" },
      { field: "nationalOffices", label: "National - Number of Offices" },
      {
        field: "internationalPlants",
        label: "International - Number of Plants",
      },
      {
        field: "internationalOffices",
        label: "International - Number of Offices",
      },
      { field: "nationalStates", label: "National - States" },
      { field: "internationalCountries", label: "International - Countries" },
    ];

    numberFields.forEach(({ field, label }) => {
      const value = formData[field];
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        isNaN(value) ||
        Number(value) < 0
      ) {
        newErrors[field] = `${label}: Enter a valid non-negative number.`;
      }
    });

    // Export percentage validation
    const exportPercentage = formData.exportPercentage;
    if (
      !exportPercentage ||
      isNaN(exportPercentage) ||
      exportPercentage < 0 ||
      exportPercentage > 100
    ) {
      newErrors.exportPercentage = percentageError;
    }

    // Customer brief validation
    if (!formData.customerBrief.trim()) {
      newErrors.customerBrief = textError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const handleSave = async () => {
    // Validate before submitting

    if (!disabled) {
      if (!validateForm()) {
        return; // Do not proceed if validation fails
      }
      try {
        const data = {
          operations: {
            ...formData1,
            numberOfLocations: {
              national: {
                plants: Number(formData.nationalPlants),
                offices: Number(formData.nationalOffices),
                total:
                  Number(formData.nationalPlants) +
                  Number(formData.nationalOffices),
              },
              international: {
                plants: Number(formData.internationalPlants),
                offices: Number(formData.internationalOffices),
                total:
                  Number(formData.internationalPlants) +
                  Number(formData.internationalOffices),
              },
            },
            marketsServed: {
              nationalStates: Number(formData.nationalStates),
              internationalCountries: Number(formData.internationalCountries),
              exportsContribution: Number(formData.exportPercentage),
              customerTypes: formData.customerBrief,
            },
          },
          operationsSubmitted: true,
          type: "SectionA",
        };

        const response = await put(`${api}/brsr/update?id=${id}`, data);
        setsnackOpen({
          open: true,
          message: response.data.message,
          severity: "success",
        });
        onSave();
      } catch (error) {
        setsnackOpen({
          open: true,
          message: error.data.message,
          severity: "error",
        });
        console.error("Error updating data:", error);
      }
    } else onSave();
  };

  console.log("Form Data:", formData);

  return (
    <Box>
      <p className={style.subHeading}>III. OPERATIONS</p>

      {/* Table for Number of Locations */}
      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", mt: 3, fontFamily: "Inter" }}
        gutterBottom
      >
        18. Number of locations 11 where plants and/or operations/offices of the
        entity are situated
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ marginLeft: "1.5rem", width: "98.2%" }}
      >
        <Table
          sx={{
            "& .MuiTableCell-root": {
              borderRight: "1px solid #e0e0e0",
              padding: "0px 16px",
            },
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#1C486B", height: "40px" }}>
              <TableCell
                sx={{
                  color: "white",
                  borderBottom: "1px solid white",
                  width: "25%",
                }}
              >
                Location
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  borderBottom: "1px solid white",
                  width: "25%",
                }}
              >
                Number of Plants
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  borderBottom: "1px solid white",
                  width: "25%",
                }}
              >
                Number of Offices
              </TableCell>
              <TableCell
                sx={{
                  color: "white",
                  borderBottom: "1px solid white",
                  width: "25%",
                }}
              >
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CustomTableRow
              label="National"
              fields={["nationalPlants", "nationalOffices"]}
              placeholder="Enter Number"
              formData={formData}
              handleInputChange={handleInputChange}
              showTotal
              errors={errors}
              disabled={disabled}
            />
            <CustomTableRow
              label="International"
              fields={["internationalPlants", "internationalOffices"]}
              placeholder="Enter Number"
              formData={formData}
              handleInputChange={handleInputChange}
              showTotal
              errors={errors}
              disabled={disabled}
            />
          </TableBody>
        </Table>
      </TableContainer>
      {Object.entries(errors)
        .filter(([field]) =>
          [
            "nationalPlants",
            "nationalOffices",
            "internationalPlants",
            "internationalOffices",
          ].includes(field),
        )
        .map(([field, error]) => (
          <Typography
            key={field}
            color="error"
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
            }}
          >
            {error}
          </Typography>
        ))}
      <BRSRFile
        {...{
          modalupload,
          setModalUpload,
          formData: formData1,
          setFormData: setFormData1,
          errors,
          setErrors,
          open,
          setOpen,
          Uploadkey: "numberOfLocationsDoc",
          ml: 3,
          removeUpload,
        }}
      />

      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", mt: 3, fontFamily: "Inter" }}
        gutterBottom
      >
        19. Markets served by the entity
      </Typography>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "15px",
          mt: 3,
          fontFamily: "Inter",
          marginLeft: "1.2rem",
        }}
        gutterBottom
      >
        a. Number of locations:
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ width: "97.7%", marginLeft: "2rem" }}
      >
        <Table
          sx={{
            "& .MuiTableCell-root": {
              borderRight: "1px solid #e0e0e0",
              padding: "4px 16px",
            },
            tableLayout: "fixed",
            width: "100%",
            borderRadius: "10px",
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#1C486B", height: "40px" }}>
              <TableCell
                sx={{ color: "white", borderBottom: "1px solid white" }}
              >
                Location
              </TableCell>
              <TableCell
                sx={{ color: "white", borderBottom: "1px solid white" }}
              >
                Numbers
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <CustomTableRow
              label="National"
              fields={["nationalStates"]}
              formData={formData}
              handleInputChange={handleInputChange}
              showTotal={false}
              placeholder="Number of states"
              errors={errors}
              disabled={disabled}
            />
            <CustomTableRow
              label="International"
              fields={["internationalCountries"]}
              placeholder="Number of Countries"
              formData={formData}
              handleInputChange={handleInputChange}
              showTotal={false}
              errors={errors}
              disabled={disabled}
            />
          </TableBody>
        </Table>
      </TableContainer>
      {Object.entries(errors)
        .filter(([field]) =>
          ["nationalStates", "internationalCountries"].includes(field),
        )
        .map(([field, error]) => (
          <Typography
            key={field}
            color="error"
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
            }}
          >
            {error}
          </Typography>
        ))}

      {/* Export Percentage */}
      <Typography
        gutterBottom
        sx={{
          fontWeight: 600,
          fontSize: "15px",
          mt: 3,
          fontFamily: "Inter",
          marginLeft: "1.2rem",
        }}
      >
        b. What is the contribution of exports as a percentage of the total
        turnover of the entity?
      </Typography>
      <TextField
        sx={{ width: "97.7%", marginLeft: "2rem" }}
        name="exportPercentage"
        value={formData.exportPercentage}
        onChange={handleInputChange}
        disabled={disabled}
        fullWidth
        size="small"
        placeholder="%"
        error={!!errors.exportPercentage}
        // helperText={errors.exportPercentage}
        helperText={
          <Typography
            variant="body2"
            color="error"
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
              marginLeft: "-10px",
            }}
          >
            {errors.exportPercentage}
          </Typography>
        }
      />

      {/* Customer Brief */}
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "15px",
          mt: 3,
          fontFamily: "Inter",
          marginLeft: "1.2rem",
        }}
        gutterBottom
      >
        c. A brief on types of customers
      </Typography>
      <TextField
        sx={{ width: "97.7%", marginLeft: "2rem" }}
        name="customerBrief"
        value={formData.customerBrief}
        onChange={handleInputChange}
        fullWidth
        size="small"
        placeholder="Brief"
        multiline
        rows={3}
        disabled={disabled}
        error={!!errors.customerBrief}
        // helperText={errors.customerBrief}
        helperText={
          <Typography
            variant="body2"
            color="error"
            style={{
              lineHeight: "1.2",
              margin: "2px 0",
              color: "#d34040",
              fontSize: "10px",
              fontStyle: "italic",
              marginLeft: "-10px",
            }}
          >
            {errors.customerBrief}
          </Typography>
        }
      />
      <BRSRFile
        {...{
          modalupload,
          setModalUpload,
          formData: formData1,
          setFormData: setFormData1,
          errors,
          setErrors,
          open,
          setOpen,
          Uploadkey: "marketsServedDoc",
          ml: 4,
          removeUpload,
        }}
      />

      {!hide && (
        <>
          {" "}
          {brsrReview ? (
            <Box sx={{ width: "97.8%", ml: 3.8 }}>
              <ReviewBox url={`/brsr/review/${id}`} handleSubmit={handleSave} />
            </Box>
          ) : (
            <Box textAlign="right" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                sx={{ bgcolor: "#1C486B", textTransform: "none", mt: 3, mb: 5 }}
                onClick={handleSave}
              >
                Save & Continue <EastIcon sx={{ ml: 2 }} />
              </Button>
            </Box>
          )}
        </>
      )}
      <Snackbar
        open={snackOpen.open}
        message={snackOpen.message} // Optional, since you're using <Alert> for the message
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          severity={snackOpen.severity || "info"} // Ensure a default severity if undefined
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message || "No message provided"}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeneralDisclosureForm;
